import { getOperacoesClienteByOperacao, createOperacaoCliente, deleteOperacaoCliente, updateOperacaoCliente } from "@spark/services/operacoes-cliente";
import { searchClientes } from "@spark/services/clientes";
import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Card, CardBody, CardTitle } from '@spark/ui-components/components/Card';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import { FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import ConfirmDeleteModal from '@/containers/UI/Modals/index2';
import { notification } from '@spark/ui-components/utils/notification';
import Icon from '@spark/ui-components/components/Icon';
import styled from 'styled-components';
import { associationFormStructure, editFormStructure, associationFormSections, editFormSections, validateAssociationForm, validateEditForm, mapFormToCreateApi, mapFormToUpdateApi, mapClienteToFormValues } from './operacaoClienteFormConfig';
import { updateMultipleFieldsByName } from '@spark/ui-components/utils/formHelpers';

const GerenciarClientesOperacao = ({
  operacaoId,
  onUpdate
}) => {
  const [clientesAssociados, setClientesAssociados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clienteParaEditar, setClienteParaEditar] = useState(null);
  const [clienteParaRemover, setClienteParaRemover] = useState(null);
  const [associando, setAssociando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [deletando, setDeletando] = useState(false); // Refs para submissão dos formulários

  const associationSubmitRef = useRef(null);
  const editSubmitRef = useRef(null);
  useEffect(() => {
    carregarClientesAssociados();
  }, [operacaoId]);

  const carregarClientesAssociados = async () => {
    try {
      setLoading(true);
      const response = await getOperacoesClienteByOperacao(operacaoId); // Simplificar o tratamento da resposta

      let operacoesCliente = [];

      if (response && response.data) {
        operacoesCliente = response.data;
      } else if (Array.isArray(response)) {
        operacoesCliente = response;
      } else {
        console.warn('Estrutura inesperada:', response);
        operacoesCliente = [];
      } // Mapear clientes


      const clientes = operacoesCliente.map(oc => ({
        Id: oc.IdCliente || oc.Id,
        Nome: oc.NomeCliente || oc.ClienteNome || 'Nome não encontrado',
        Cnpj: oc.CnpjCliente || oc.Cnpj || '',
        Telefone: oc.TelefoneCliente || oc.Telefone || '',
        Observacao: oc.ObservacaoCliente || oc.Observacao || '',
        IdAssociacao: oc.Id,
        DiasRecorrencia: oc.DiasRecorrencia || 0,
        RecorrenciaLigada: oc.RecorrenciaLigada || false,
        ValorIndividualPallet: oc.ValorIndividualPallet ? parseFloat(oc.ValorIndividualPallet) : null,
        ValorEtiquetas: oc.ValorEtiquetas ? parseFloat(oc.ValorEtiquetas) : null,
        ValorArmazenagem: oc.ValorArmazenagem ? parseFloat(oc.ValorArmazenagem) : null,
        Freetime: oc.Freetime ? parseInt(oc.Freetime) : null,
        DataVigencia: oc.DataVigencia || null
      }));
      setClientesAssociados(clientes);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarClientes = async searchTerm => {
    try {
      const response = await searchClientes(searchTerm);

      if (Array.isArray(response)) {
        // Filtrar clientes que não estão associados
        const clientesAssociadosIds = clientesAssociados.map(c => c.Id);
        const disponiveis = response.filter(c => !clientesAssociadosIds.includes(c.Id));
        return disponiveis.map(cliente => ({
          value: cliente.Id,
          label: `${cliente.Nome} - ${formatCNPJ(cliente.Cnpj)}`
        }));
      }

      return [];
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      return [];
    }
  };

  const handleAssociarCliente = async values => {
    try {
      setAssociando(true);
      const payload = mapFormToCreateApi(values);
      payload.IdOperacao = operacaoId;
      await createOperacaoCliente(payload);
      notification.success('Sucesso', 'Cliente associado com sucesso'); // Aguardar recarregamento antes de fechar modal para evitar race condition

      await carregarClientesAssociados(); // Fechar modal após operações completadas

      setShowModal(false);
    } catch (error) {
      console.error('Erro ao associar cliente:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      setAssociando(false);
    }
  };

  const handleEditarCliente = async values => {
    if (!clienteParaEditar) {
      notification.error('Erro', 'Cliente não encontrado');
      return;
    }

    try {
      setEditando(true);
      const payload = mapFormToUpdateApi(values);
      await updateOperacaoCliente(clienteParaEditar.IdAssociacao, payload);
      notification.success('Sucesso', 'Dados do cliente atualizados com sucesso'); // Aguardar recarregamento antes de fechar modal para evitar race condition

      await carregarClientesAssociados(); // Fechar modal após operações completadas

      setShowEditModal(false);
      setClienteParaEditar(null);
    } catch (error) {
      console.error('Erro ao editar cliente:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      setEditando(false);
    }
  };

  const handleDesassociarCliente = async cliente => {
    setClienteParaRemover(cliente);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!clienteParaRemover) return;

    try {
      setDeletando(true); // Usar o ID da associação para remover

      await deleteOperacaoCliente(clienteParaRemover.IdAssociacao);
      notification.success('Sucesso', 'Cliente desassociado com sucesso');
      carregarClientesAssociados();
    } catch (error) {
      console.error('Erro ao desassociar cliente:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      setDeletando(false);
      setShowDeleteModal(false);
      setClienteParaRemover(null);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (!associando) {
      setShowModal(false);
    }
  };

  const handleOpenEditModal = cliente => {
    setClienteParaEditar(cliente);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    if (!editando) {
      setShowEditModal(false);
      setClienteParaEditar(null);
    }
  };

  const formatCNPJ = cnpj => {
    if (!cnpj || typeof cnpj !== 'string') return '-';
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) return cnpj;
    return cnpjLimpo.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  };

  const formatCurrency = value => {
    if (!value || typeof value !== 'number') return 'R$ 0,00';
    return `R$ ${parseFloat(value).toFixed(2).replace('.', ',')}`;
  };

  const columns = [{
    id: 'nome',
    accessorKey: 'Nome',
    header: 'Nome',
    size: 250,
    cell: ({
      row
    }) => row.original.Nome || 'Nome não encontrado'
  }, {
    id: 'cnpj',
    accessorKey: 'Cnpj',
    header: 'CNPJ',
    size: 150,
    cell: ({
      row
    }) => formatCNPJ(row.original.Cnpj)
  }, {
    id: 'telefone',
    accessorKey: 'Telefone',
    header: 'Telefone',
    size: 150,
    cell: ({
      row
    }) => row.original.Telefone || '-'
  }, {
    id: 'observacao',
    accessorKey: 'Observacao',
    header: 'Observação',
    size: 200,
    cell: ({
      row
    }) => row.original.Observacao || '-'
  }, {
    id: 'recorrencia',
    header: 'Recorrência',
    size: 120,
    cell: ({
      row
    }) => {
      const cliente = row.original;

      if (cliente.RecorrenciaLigada && typeof cliente.DiasRecorrencia === 'number') {
        return `${cliente.DiasRecorrencia} dias`;
      }

      return 'Não aplicável';
    }
  }, {
    id: 'statusRecorrencia',
    header: 'Status Recorrência',
    size: 140,
    cell: ({
      row
    }) => {
      const cliente = row.original;
      const isRecorrenciaLigada = Boolean(cliente.RecorrenciaLigada);
      return <span style={{
        color: isRecorrenciaLigada ? '#28a745' : '#6c757d',
        fontWeight: isRecorrenciaLigada ? 'bold' : 'normal'
      }}>
          
            {isRecorrenciaLigada ? 'Ativa' : 'Inativa'}
          </span>;
    }
  }, {
    id: 'valorPallet',
    header: 'Valor Pallet',
    size: 120,
    cell: ({
      row
    }) => formatCurrency(row.original.ValorIndividualPallet)
  }, {
    id: 'valorEtiquetas',
    header: 'Valor Etiquetas',
    size: 120,
    cell: ({
      row
    }) => formatCurrency(row.original.ValorEtiquetas)
  }, {
    id: 'valorArmazenagem',
    header: 'Valor Armazenagem',
    size: 130,
    cell: ({
      row
    }) => formatCurrency(row.original.ValorArmazenagem)
  }, {
    id: 'freetime',
    header: 'Freetime',
    size: 100,
    cell: ({
      row
    }) => {
      const cliente = row.original;
      return cliente.Freetime && typeof cliente.Freetime === 'number' ? `${cliente.Freetime} dias` : 'Não definido';
    }
  }, {
    id: 'dataVigencia',
    header: 'Data Vigência',
    size: 120,
    cell: ({
      row
    }) => {
      const cliente = row.original;
      if (!cliente.DataVigencia) return 'Não definida';

      try {
        const data = new Date(cliente.DataVigencia);
        if (isNaN(data.getTime())) return 'Data inválida';
        return data.toLocaleDateString('pt-BR');
      } catch {
        return 'Data inválida';
      }
    }
  }, {
    id: 'acoes',
    header: 'Ações',
    size: 150,
    cell: ({
      row
    }) => <div style={{
      display: 'flex',
      gap: '5px'
    }}>
          <ButtonWithLoading variant="outline-primary" size="sm" onClick={() => handleOpenEditModal(row.original)} loading={false}>
        
            <Icon name="pencil" size={14} />
            Editar
          </ButtonWithLoading>
          <ButtonWithLoading variant="outline-danger" size="sm" onClick={() => handleDesassociarCliente(row.original)} loading={false}>
        
            <Icon name="account-remove" size={14} />
            Remover
          </ButtonWithLoading>
        </div>
  }];
  return <>
      <Card>
        <CardBody>
          <HeaderWrap>
            <CardTitle>Clientes Associados</CardTitle>
            <ButtonWithLoading variant="primary" onClick={handleOpenModal} loading={false}>
              
              <Icon name="account-plus" size={16} />
              Associar Cliente
            </ButtonWithLoading>
          </HeaderWrap>

          <TanStackTable columns={columns} data={clientesAssociados} loading={loading} withPagination={true} pageSizeOptions={[10, 25, 50]} enableRowColors={true} className="mt-3" />
          
        </CardBody>
      </Card>

      {
      /* Modal para associar cliente */
    }
      <Modal show={showModal} onHide={handleCloseModal} animation={false} size="lg" centered backdrop="static" keyboard={false} style={{
      zIndex: 1055
    }}>
        
        <Modal.Header closeButton>
          <Modal.Title>Associar Cliente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAssociarCliente} validate={validateAssociationForm} initialValues={{
          cliente: null,
          diasRecorrencia: '',
          recorrenciaLigada: false,
          valorIndividualPallet: '',
          valorEtiquetas: '',
          valorArmazenagem: '',
          freetime: '',
          dataVigencia: ''
        }}>

            
            {({
            handleSubmit
          }) => {
            // Store the submit function for external access
            associationSubmitRef.current = handleSubmit; // Update form structure with search functions

            const updatedFormStructure = updateMultipleFieldsByName(associationFormStructure, {
              cliente: {
                onSearch: handleBuscarClientes
              }
            });
            return <FormContainer onSubmit={handleSubmit}>
                  <DynamicForm structure={updatedFormStructure} sections={associationFormSections} />
                  
                </FormContainer>;
          }}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={associando}>
            
            Cancelar
          </Button>
          <ButtonWithLoading variant="primary" onClick={() => associationSubmitRef.current && associationSubmitRef.current()} loading={associando}>
            
            Associar
          </ButtonWithLoading>
        </Modal.Footer>
      </Modal>

      {
      /* Modal para editar cliente */
    }
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg" centered backdrop="static" keyboard={false} style={{
      zIndex: 1055
    }}>
        
        <Modal.Header closeButton>
          <Modal.Title>Editar Cliente - {clienteParaEditar?.Nome}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditarCliente} validate={validateEditForm} initialValues={clienteParaEditar ? mapClienteToFormValues(clienteParaEditar) : {
          diasRecorrencia: '',
          recorrenciaLigada: false,
          valorIndividualPallet: '',
          valorEtiquetas: '',
          valorArmazenagem: '',
          freetime: '',
          dataVigencia: ''
        }}>

            
            {({
            handleSubmit
          }) => {
            // Store the submit function for external access
            editSubmitRef.current = handleSubmit;
            return <FormContainer onSubmit={handleSubmit}>
                  <DynamicForm structure={editFormStructure} sections={editFormSections} />
                  
                </FormContainer>;
          }}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal} disabled={editando}>
            
            Cancelar
          </Button>
          <ButtonWithLoading variant="primary" onClick={() => editSubmitRef.current && editSubmitRef.current()} loading={editando}>
            
            Salvar
          </ButtonWithLoading>
        </Modal.Footer>
      </Modal>

      {
      /* Modal de confirmação de exclusão padrão do sistema */
    }
      <ConfirmDeleteModal show={showDeleteModal} onClose={() => {
      setShowDeleteModal(false);
      setClienteParaRemover(null);
    }} onConfirm={handleConfirmDelete} loading={deletando} />
      
    </>;
};

const HeaderWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;
export default GerenciarClientesOperacao;
